import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import { ClipLoader } from "react-spinners";

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};

// Slugifies each h2/h3/h4 in the article body and returns the modified HTML
// plus a flat list of {id, text, level} entries to render as a table of contents.
const extractToc = (html) => {
  if (!html) return { html, toc: [] };
  const doc = new DOMParser().parseFromString(html, "text/html");
  const headings = doc.querySelectorAll("h2, h3, h4");
  const seen = new Set();
  const toc = [];
  headings.forEach((h) => {
    const text = h.textContent.trim();
    if (!text) return;
    const base = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    let id = base || "section";
    let i = 2;
    while (seen.has(id)) id = `${base}-${i++}`;
    seen.add(id);
    h.id = id;
    toc.push({ id, text, level: Number(h.tagName[1]) });
  });
  return { html: doc.body.innerHTML, toc };
};

const HelpArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [bodyHtml, setBodyHtml] = useState("");
  const [toc, setToc] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API_BASE_URL}help-center/articles/${slug}`);
        setArticle(res.data.data);
        const { html, toc } = extractToc(res.data.data?.body);
        setBodyHtml(html);
        setToc(toc);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        console.error("Failed to load guide", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh", width: "100%" }}>
        <ClipLoader size={45} />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <>
      <div className="innerbanner"></div>
      <section className="py-5 text-center" style={{ background: "#eef6f4" }}>
        <div className="container">
          <h4 style={{ color: "#2b2b52" }}>Guide not found</h4>
          <p className="text-muted">This guide may have been moved or unpublished.</p>
          <Link to="/help-center" className="theme-btn">Back to Help Center</Link>
        </div>
      </section>
      </>
    );
  }

  return (
    <>
    <div className="innerbanner"></div>
    <section className="py-5" style={{ background: "#eef6f4" }}>
      <div className="container">
        <div className="mb-4 small">
          <Link to="/help-center" style={{ color: "#2f6b4f", fontWeight: 600, textDecoration: "none" }}>Help Center</Link>
          <span className="text-muted"> / {article.category || "Guide"}</span>
        </div>

        <div className="row">
          <div className="col-lg-8">
            <div className="p-4 p-md-5 rounded shadow-sm" style={{ background: "#fff" }}>
              {article.category && (
                <span
                  className="badge rounded-pill mb-3"
                  style={{ background: "#e5f1ea", color: "#2f6b4f", fontWeight: 600, letterSpacing: "0.03em" }}
                >
                  {article.category}
                </span>
              )}
              <h2 className="fw-bold mb-2" style={{ color: "#2b2b52" }}>{article.title}</h2>
              <div className="text-muted small mb-4 pb-3" style={{ borderBottom: "1px solid #dde8e4" }}>
                Updated {formatDate(article.updated_at)}
              </div>

              <div
                style={{ lineHeight: 1.75, color: "#232a2e" }}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </div>
          </div>

          <div className="col-lg-4 mt-4 mt-lg-0">
            <div style={{ position: "sticky", top: 20 }}>
              {toc.length > 0 && (
                <div className="p-4 rounded shadow-sm mb-4" style={{ background: "#fff" }}>
                  <h6 className="fw-bold mb-3" style={{ color: "#2b2b52" }}>Contents</h6>
                  <ul className="list-unstyled small mb-0">
                    {toc.map((item) => (
                      <li
                        key={item.id}
                        style={{ marginLeft: (item.level - Math.min(...toc.map((t) => t.level))) * 14 }}
                        className="mb-2"
                      >
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleTocClick(e, item.id)}
                          style={{ color: "#2f6b4f", textDecoration: "none" }}
                        >
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="p-4 rounded shadow-sm" style={{ background: "#fff" }}>
                <h6 className="fw-bold mb-2" style={{ color: "#2b2b52" }}>Still stuck?</h6>
                <p className="text-muted small mb-3">Our Siliguri team is a phone call away.</p>
                <a href="/contact" className="theme-btn d-block text-center">Contact Support</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default HelpArticleDetail;
